import React, { useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Heading from 'components/partials/heading/heading';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import Button from 'components/partials/button/button';
import Loader from 'components/partials/loader/loader';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import AidStudioStepper from './components/aid-studio-stepper';
import BasicInfoStep from './steps/basic-info-step';
import FundsStep from './steps/funds-step';
import TuitionStep from './steps/tuition-step';
import MatrixStep from './steps/matrix-step';

import { useAidStudioForm } from './hooks/use-aid-studio-form';

import { AID_STUDIO_STEPS } from './aid-studio-details.constants';

import Icons from 'assets/icons';

const STEP_ACTIONS_SLOT_ID = 'step-actions-slot';
const STEP_VALUES = AID_STUDIO_STEPS.map((s) => s.value);
const DEFAULT_STEP = AID_STUDIO_STEPS[0].value;

interface ActiveStepRef {
  hasChanges?: boolean;
  save?: () => Promise<void> | void;
}

const AidStudioDetails = (): React.JSX.Element | null => {
  const { clientId, modelId, step: stepParam } = useParams();
  const navigate = useNavigate();
  const activeStepRef = useRef<ActiveStepRef | null>(null);

  const basePath = modelId
    ? `/admin/clients/${clientId}/aid-studio/${modelId}`
    : `/admin/clients/${clientId}/aid-studio/new`;

  const setActiveStep = useCallback(
    (step: string): void => {
      const path = step === DEFAULT_STEP ? basePath : `${basePath}/${step}`;

      navigate(path, { replace: true });
    },
    [navigate, basePath]
  );

  const {
    control,
    errors,
    isValid,
    isDirty,
    handleSubmit,
    isNewModel,
    isSaving,
    isLoading,
    isError,
    modelData,
    datasetData,
    sourceTablesOptions,
    sourceTablesLoading,
    sourceTablesFetching,
    recreateDataset,
    handleRecreateDatasetChange,
    handleSave,
    handleCancel,
    handleDiscard,
  } = useAidStudioForm({
    clientId: clientId ?? '',
    modelId,
  });

  const pageTitle = isNewModel
    ? 'New aid model'
    : modelData?.name || 'Aid model';

  const onSubmit = (data: Parameters<typeof handleSave>[0]): Promise<void> =>
    handleSave(data, false);

  const basicInfoSave = useCallback(
    () =>
      handleSubmit(async (data) => {
        await handleSave(data, false);
      })(),
    [handleSubmit, handleSave]
  );

  const activeStep = isNewModel
    ? DEFAULT_STEP
    : stepParam && STEP_VALUES.includes(stepParam)
      ? stepParam
      : DEFAULT_STEP;

  const visibleSteps = useMemo(
    () =>
      isNewModel
        ? AID_STUDIO_STEPS.map((s, i) =>
            i === 0 ? s : { ...s, isDisabled: true }
          )
        : AID_STUDIO_STEPS,
    [isNewModel]
  );

  const activeStepIndex = AID_STUDIO_STEPS.findIndex(
    (s) => s.value === activeStep
  );

  const saveCurrentStep = useCallback(async (): Promise<void> => {
    const step = activeStepRef.current;

    if (step?.hasChanges) {
      await step.save?.();
    }
  }, []);

  const handleBack = async (): Promise<void> => {
    if (activeStepIndex > 0) {
      await saveCurrentStep();
      setActiveStep(AID_STUDIO_STEPS[activeStepIndex - 1].value);
    }
  };

  const handleNext = async (): Promise<void> => {
    if (activeStepIndex < AID_STUDIO_STEPS.length - 1) {
      const nextStep = AID_STUDIO_STEPS[activeStepIndex + 1];

      if (!nextStep.isDisabled) {
        await saveCurrentStep();
        setActiveStep(nextStep.value);
      }
    }
  };

  const handleStepChange = useCallback(
    async (stepValue: string): Promise<void> => {
      if (stepValue !== activeStep) {
        await saveCurrentStep();
        setActiveStep(stepValue);
      }
    },
    [activeStep, saveCurrentStep, setActiveStep]
  );

  const renderStepContent = (): React.JSX.Element | null => {
    switch (activeStep) {
      case 'basic-info':
        return (
          <BasicInfoStep
            ref={activeStepRef as never}
            control={control as never}
            errors={errors as never}
            disabled={false}
            isDirty={isDirty}
            isValid={isValid}
            onSave={basicInfoSave}
            sourceTablesOptions={sourceTablesOptions}
            sourceTablesLoading={sourceTablesLoading}
            sourceTablesFetching={sourceTablesFetching}
            isNewModel={isNewModel}
            recreateDataset={recreateDataset}
            onRecreateDatasetChange={handleRecreateDatasetChange}
            datasetData={datasetData}
          />
        );
      case 'funds':
        return (
          <FundsStep
            ref={activeStepRef as never}
            stepActionsSlotId={STEP_ACTIONS_SLOT_ID}
          />
        );
      case 'tuition':
        return (
          <TuitionStep
            ref={activeStepRef as never}
            stepActionsSlotId={STEP_ACTIONS_SLOT_ID}
          />
        );
      case 'matrix':
        return (
          <MatrixStep
            ref={activeStepRef as never}
            stepActionsSlotId={STEP_ACTIONS_SLOT_ID}
          />
        );
      case 'exceptions':
      case 'results':
        return (
          <div className="py-8 text-center text-gray-600">Coming soon...</div>
        );
      default:
        return null;
    }
  };

  if (isError && !isNewModel) {
    return (
      <ResponsiveWrapper>
        <div className="text-center py-8">
          <p className="text-critical-600">Failed to load aid model</p>
          <Button variant="reserve-text" onClick={handleCancel}>
            Go back
          </Button>
        </div>
      </ResponsiveWrapper>
    );
  }

  const isFirstStep = activeStepIndex === 0;
  const nextStep = AID_STUDIO_STEPS[activeStepIndex + 1];
  const isNextDisabled = isNewModel || !nextStep || nextStep.isDisabled;

  return (
    <ResponsiveWrapper>
      <Loader loading={isLoading || isSaving} />

      <Breadcrumbs
        breadcrumbs={[
          {
            label: 'Aid studio',
            href: `/admin/clients/${clientId}/aid-studio`,
          },
          {
            label: isNewModel
              ? 'New aid model'
              : modelData?.name || 'Aid model',
            isActive: true,
          },
        ]}
      />

      <Heading variant="h1" customClass="block my-5">
        {pageTitle}
      </Heading>

      <div className="flex items-center justify-between mb-6">
        <AidStudioStepper
          steps={visibleSteps}
          activeStep={activeStep}
          onStepChange={handleStepChange}
        />
        <div
          id={STEP_ACTIONS_SLOT_ID}
          className="flex items-center gap-3 flex-shrink-0 ml-4"
        >
          {activeStep === 'basic-info' && (
            <>
              <Button
                type="button"
                variant="reserve-text"
                size="sm"
                onClick={handleDiscard}
                disabled={!isDirty && !recreateDataset}
                data-testid="discard-changes-button"
              >
                Discard changes
              </Button>
              <Button
                type="button"
                variant="brand-filled"
                size="sm"
                onClick={handleSubmit(onSubmit)}
                disabled={(!isDirty && !recreateDataset) || !isValid || isSaving}
                loading={isSaving}
                data-testid="save-button"
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {renderStepContent()}

        <div className="flex items-center justify-between mt-8">
          {!isFirstStep ? (
            <Button
              type="button"
              variant="reserve-text"
              size="md"
              onClick={handleBack}
              className="text-navy-600"
              iconBefore={
                <Icons.ChevronLeft />
              }
              data-testid="back-button"
            >
              Back
            </Button>
          ) : (
            <div />
          )}

          <Button
            type="button"
            variant="reserve-text"
            size="md"
            onClick={handleNext}
            disabled={isNextDisabled}
            className="text-navy-600"
            iconAfter={
              <Icons.ChevronRight />
            }
            data-testid="next-button"
          >
            Next
          </Button>
        </div>
      </form>
    </ResponsiveWrapper>
  );
};

export default AidStudioDetails;
